import React, { useState } from 'react';
import authFetch from '../utils/authFetch';
import { CONSENT_VERSION } from '../config/consent';
import { LEGAL_LAST_UPDATED } from '../config/legal';

// ==================== LEGAL DOCUMENTS ====================
// privacy = the Hebrew Privacy Policy, kept identical to the public /privacy page (PrivacyPolicy.jsx;
// legal.texts.test.jsx compares them). Revision history and archived versions: LEGAL_REVISIONS.md.

export const LEGAL_DOCS = {
  health: {
    title: "הצהרת בריאות",
    content: `
## הצהרת בריאות

בסימון ההסכמה אני מאשר/ת שקראתי והבנתי את הדברים הבאים:

### פעילות גופנית כרוכה בסיכון
• ריצה ופעילות גופנית כרוכות בסיכון מובנה לפציעה ולבעיות בריאותיות, גם כשמתאמנים לפי תכנית.
• ההחלטה אם מצבי הבריאותי מאפשר לי להתאמן, ובאיזו עצימות, היא באחריותי. RitmoIQ אינה קובעת ואינה יכולה לקבוע כשירות רפואית לפעילות גופנית.

### התייעצות רפואית
• מומלץ להתייעץ עם רופא לפני תחילת תכנית אימונים, ובמיוחד אם יש לי מצב רפואי, פציעה, כאב מתמשך או ספק כלשהו לגבי יכולתי להתאמן.
• אפשר להשתמש ב-RitmoIQ גם עם פציעה או מגבלה. דיווח עליהן באפליקציה עוזר להתאים את ההמלצות, אך אינו מחליף הנחיה של רופא או איש מקצוע בריאות.

### בטיחות במהלך האימון
• אפסיק את הפעילות ואפנה לייעוץ רפואי מתאים אם אחוש כאב, אי-נוחות חריגה, סחרחורת, קוצר נשימה, כאב בחזה או כל תסמין מדאיג אחר.
• במצב חירום רפואי אפנה מיד לשירותי החירום (מד"א 101).

### מה RitmoIQ אינה
• RitmoIQ מספקת המלצות אימון שנוצרות בין היתר באמצעות AI, למטרות מידע בלבד.
• היא אינה ייעוץ רפואי, אינה מאבחנת או מטפלת במצבים רפואיים, ואינה שירות חירום.
• היא אינה מחליפה רופא, פיזיותרפיסט או מאמן מוסמך כאשר נדרשת הכוונה כזו.
• ההמלצות עלולות לכלול טעויות, ואין בהן הבטחה למניעת פציעות או לתוצאה בריאותית או ספורטיבית מסוימת.

### אחריות
• אפעיל שיקול דעת לגבי ההמלצות, ואתאים או אפסיק אימון בהתאם לתחושתי ולהנחיות רפואיות.
• הגבלת האחריות של RitmoIQ מפורטת בסעיף 7 לתנאי השימוש וחלה רק ככל שהדין מאפשר. אין באמור כדי לשלול זכויות או אחריות שלא ניתן לשלול על פי דין.
    `
  },
  privacy: {
    title: "מדיניות פרטיות",
    content: `
עודכן לאחרונה: ${LEGAL_LAST_UPDATED.he}

## 1. מבוא

RitmoIQ ("האפליקציה", "אנחנו", "שלנו") מחויבת להגן על פרטיות המשתמשים שלנו. RitmoIQ הוא פרויקט המופעל באופן פרטי על ידי ליאור פרובקביץ, ואינו חברה מאוגדת. מדיניות פרטיות זו מסבירה כיצד אנו אוספים, משתמשים ומגנים על המידע שלך בעת השימוש באפליקציה שלנו.

## 2. מידע שאנו אוספים

### 2.1 מידע שאתה מספק לנו:
• שם ופרטי קשר (אימייל)
• פרטי פרופיל ריצה (תאריך לידה, רמה, שיאים אישיים). תאריך הלידה נשמר כדי לחשב את הגיל שלך באופן אוטומטי; משתמשים ותיקים שלא הזינו תאריך לידה — הגיל שהוזן נשמר כפי שהוא
• מידע גופני ובריאותי הרלוונטי לאימון, אם תבחר/י למסור אותו (כגון משקל, דופק מרבי והיסטוריית פציעות)
• יעדי אימון ומרוצים
• שיחות עם מאמן ה-AI

### 2.2 מידע שהתקבל מ-Strava (החיבור הופסק):
משתמשים שחיברו בעבר את Strava נתנו הסכמה לקבלת המידע הבא מ-Strava:
• נתוני פעילויות ריצה (תאריך, מרחק, זמן, קצב, דופק וספליטים)
• שם פרופיל ותמונה מ-Strava
• היסטוריית אימונים
החל מ-21 באוקטובר 2026 איננו מקבלים מידע חדש מ-Strava (ראו סעיף 8).

### 2.3 מידע טכני:
• כתובת IP
• סוג דפדפן ומכשיר
• זמני גישה לאפליקציה

### 2.4 התחברות עם Google:
אם בחרת להתחבר עם Google, Google מעבירה לשירות ההתחברות שלנו (Firebase Authentication, שירות של Google) את כתובת האימייל, השם ותמונת הפרופיל של חשבון Google שלך. RitmoIQ משתמשת בכתובת האימייל ובמזהה החשבון כדי לזהות אותך ולאמת את הגישה לפיילוט. השם ותמונת הפרופיל מחשבון Google אינם נשמרים במסד הנתונים של RitmoIQ ואינם נשלחים לספקי ה-AI. איננו מקבלים את הסיסמה של חשבון Google שלך.
ההתחברות עם Google היא לבחירתך: אפשר גם להתחבר עם אימייל וסיסמה, ולהגדיר סיסמה מתוך הפרופיל. ניתן להסיר את הגישה של RitmoIQ בהגדרות האבטחה של חשבון Google שלך; הסרה זו אינה מוחקת את החשבון שלך ב-RitmoIQ (ראו סעיף 7).

## 3. כיצד אנו משתמשים במידע

• אימון מותאם אישית: ניתוח האימונים שלך ויצירת תכניות אימון מותאמות
• משוב ותובנות AI: הפקת ניתוח אימונים, משוב ותובנות מותאמים אישית באמצעות בינה מלאכותית (ראו סעיף 4.1)
• מעקב התקדמות: הצגת נתונים וסטטיסטיקות על הריצות שלך
• תקשורת: שליחת התראות ועדכונים על האימונים שלך
• שיפור השירות: ניתוח שימוש כללי לשיפור האפליקציה

## 4. שיתוף מידע עם צדדים שלישיים

✅ אנחנו לא מוכרים, משכירים או משתפים את המידע האישי שלך עם צדדים שלישיים למטרות שיווק.

אנו משתפים מידע רק במקרים הבאים:
• ספקי שירות: שרתים ותשתיות (Render, Vercel) לצורך הפעלת האפליקציה, ו-Google Firebase להתחברות (Firebase Authentication) ולהתראות (Firebase Cloud Messaging)
• AI: OpenAI, באמצעות שירות OpenAI API, ו-Google, באמצעות שירות Gemini API, כספקי עיבוד בינה מלאכותית (ראו סעיף 4.1)
• Garmin (פיילוט סגור): Stridee, כספק החיבור ל-Garmin Connect (ראו סעיף 9)
• דרישה חוקית: אם נדרש על פי חוק

### 4.1 עיבוד באמצעות בינה מלאכותית (OpenAI API ו-Google Gemini API)
RitmoIQ משתמשת בשני ספקי עיבוד בינה מלאכותית, בשירותי ה-API בתשלום שלהם, כדי להפיק עבורך אימון מותאם אישית, ניתוח אימונים ותובנות: OpenAI (OpenAI API) — המודל הראשי של מאמן ה-AI בשיחות; ו-Google (Gemini API) — בשיחות שבהן מדווח כאב, פציעה או חשד לפציעה, לניתוח עומק של אימון, לתובנות ולהתאמת תוכנית האימונים, וכגיבוי כאשר השירות של OpenAI אינו זמין.
כאשר הדבר נדרש לצורך האימון שלך, המידע הבא עשוי להישלח לספקים אלה:
• פרטי פרופיל ריצה (כגון רמה, וכן גיל — מחושב מתאריך הלידה — ומשקל אם נמסרו). תאריך הלידה המלא אינו נשלח לספקי ה-AI, רק הגיל המחושב
• יעדי אימון ומרוצים
• היסטוריית פציעות רלוונטית
• דופק מרבי
• תוכנית האימונים שלך
• נתוני אימונים ופעילויות מ-Garmin, למשתמשים שאישרו את סעיף ה-AI בהסכמת Garmin (ראו סעיף 9), כגון תאריך, מרחק, משך, קצב, דופק וספליטים
• הודעות צ'אט רלוונטיות
• שמך כפי שהוזן בפרופיל (בדרך כלל שם פרטי)
בהתאם למימוש הנוכחי, כתובת האימייל, נתוני GPS/מסלול, תמונות ומידע שהתקבל מ-Strava (לרבות סיכומים וניתוחים שהופקו ממנו) אינם נשלחים לספקי ה-AI.
RitmoIQ אינה משתמשת במידע שלך כדי לאמן מודלי AI משלה. עיבוד המידע על ידי OpenAI כפוף לתנאי השימוש של OpenAI API (https://openai.com/policies/services-agreement/), ועיבוד המידע על ידי Google כפוף לתנאי השימוש של Gemini API (https://ai.google.dev/gemini-api/terms).

## 5. אבטחת מידע

אנו נוקטים באמצעים לאבטחת המידע שלך:
• הצפנת נתונים בתעבורה (HTTPS)
• אחסון מאובטח בשרתים מוגנים
• גישה מוגבלת למידע רגיש
• אימות משתמשים באמצעות Firebase Authentication (אימייל וסיסמה, או Google)

## 6. הזכויות שלך (GDPR)

יש לך את הזכויות הבאות לגבי המידע שלך:
• זכות גישה: לבקש עותק של המידע שאנו מחזיקים עליך
• זכות תיקון: לתקן מידע שגוי
• זכות מחיקה: לבקש מחיקת כל המידע שלך
• זכות ניוד: לקבל את המידע שלך בפורמט קריא
• זכות התנגדות: להתנגד לעיבוד מסוים של המידע
למימוש זכויות אלה, פנה אלינו בכתובת: support@ritmoiq.com

## 7. מחיקת חשבון ונתונים

תוכל/י למחוק את החשבון והנתונים שלך בכל עת:
• מתוך האפליקציה: פרופיל ← מחיקת חשבון
• בפנייה אלינו בכתובת: support@ritmoiq.com
מחיקת החשבון מתוך הפרופיל מוחקת את נתוני החשבון שלך ממסד הנתונים של RitmoIQ, לרבות הפרופיל, תוכניות האימון, ניתוחי האימונים, היסטוריית הצ'אט, נתוני Garmin ומידע שהתקבל בעבר מ-Strava, וכן את חשבון ההתחברות שלך (Firebase). לאחר המחיקה לא ניתן לשחזר את החשבון. אם שלב כלשהו במחיקה לא הושלם, תוצג לך הודעה ותוכל/י לנסות שוב.
אם חיברת בעבר את Strava, אנו מבקשים גם לבטל את ההרשאה של RitmoIQ בחשבון ה-Strava שלך; אם הביטול לא מצליח, נציג לך הנחיה להסיר את RitmoIQ ידנית בהגדרות Strava. אם חיברת את Garmin, אנו מבקשים מ-Stridee למחוק את החשבון שלך אצלה.
עותקים של מידע שנמחק עשויים להישמר בגיבויים של ספקי התשתית שלנו לפרק זמן נוסף, בהתאם למחזורי הגיבוי שלהם.
מידע שהתקבל בעבר מ-Strava ומידע שהופק ממנו עשויים להישמר עד למחיקת החשבון או עד לבקשת מחיקה נפרדת. הסרת ההרשאה של RitmoIQ בהגדרות Strava אינה מוחקת בעצמה מידע שכבר נשמר אצלנו.

## 8. Strava (החיבור הופסק)

בעבר אפשרה האפליקציה לחבר חשבון Strava באמצעות Strava API, בהתאם לתנאי השימוש של Strava (https://www.strava.com/legal/api). ב-21 באוקטובר 2026 החיבור הופסק: לא ניתן עוד לחבר את Strava ל-RitmoIQ או לנהל את החיבור מתוך האפליקציה, ואיננו מקבלים מידע חדש מ-Strava.
• מידע שהתקבל בעבר מ-Strava עשוי להישמר עד למחיקת החשבון או עד לבקשת מחיקה נפרדת (ראו סעיף 7). בהתאם למימוש הנוכחי, הוא אינו נשלח לספקי ה-AI.
• אם חיברת בעבר את Strava, ייתכן שההרשאה של RitmoIQ עדיין מופיעה בחשבון ה-Strava שלך. ניתן להסיר אותה בכל עת בהגדרות Strava, בעמוד My Apps.
• איננו משנים או מוחקים נתונים בחשבון ה-Strava שלך.

## 9. Garmin Connect (פיילוט סגור)

החיבור ל-Garmin Connect באמצעות Stridee זמין כרגע למשתמשים שאושרו לפיילוט הסגור של RitmoIQ, והוא לבחירתך בלבד.
• איך זה עובד: החיבור מתבצע באמצעות Stridee, ספק צד שלישי שמחבר את RitmoIQ ל-Garmin Connect. את החיבור מאשרים בדף ההסכמה של Stridee ו-Garmin. Stridee מקבלת מאיתנו מזהה אקראי, ולא את כתובת האימייל או את פרטי החשבון שלך ב-RitmoIQ. רק לאחר שאישרת את החיבור, Stridee מעבירה לנו את הפעילויות שהושלמו.
• מה אנחנו מקבלים: לכל פעילות, את הקובץ המקורי של השעון (קובץ FIT). הקובץ עשוי לכלול נתוני מיקום (GPS).
• מה אנחנו שומרים: הקובץ המקורי נשמר מוצפן ומוגדר להימחק אוטומטית לאחר 14 יום. מהקובץ אנו מפיקים סיכום אימון, כגון מועד, מרחק, זמן, קצב, דופק, הקפות, ספליטים לק"מ ומדדי ריצה שהשעון רשם (כגון קדנס). הסיכום נשמר ללא נתוני מיקום וללא המספר הסידורי של המכשיר. מאמן ה-AI מנתח פעילויות ריצה בלבד.
• למה משמשים הנתונים: נתוני Garmin משמשים כדי להציג את הפעילויות שלך, לנתח אותן ולהשוות אותן לאימונים המתוכננים בתוכנית שלך, וכדי לשלוח לך התראה על אימון שהסתיים אם הפעלת התראות. ניתוח באמצעות AI נעשה רק בהסכמה נפרדת (ראו להלן).
• ניתוח באמצעות AI (בהסכמה נפרדת): אם אישרת את סעיף ה-AI בהסכמת Garmin, מאמן ה-AI משתמש בסיכומי האימון האלה לניתוח אישי, להמלצות ולהתאמת תוכנית האימונים שלך. לשם כך נתוני אימון (כגון תאריך, מרחק, זמן, קצב, דופק והקפות) נשלחים לספקי ה-AI, OpenAI ו-Google Gemini, כמתואר בסעיף 4.1. נתוני מיקום, הקובץ המקורי, המספר הסידורי של המכשיר ומזהים פנימיים אינם נשלחים. בלי הסכמה זו, נתוני Garmin אינם נשלחים לשירות AI. RitmoIQ אינה משתמשת בנתונים אלה כדי לאמן מודלי AI משלה.
• ביטול הסכמת ה-AI: ניתן לבטל את סעיף ה-AI בכל עת מתוך הפרופיל (חיבורי אימונים ← ניהול חיבור Garmin ← ביטול ההסכמה לניתוח AI), בפנייה אלינו בכתובת support@ritmoiq.com או בניתוק Garmin. לאחר הביטול נתוני Garmin אינם נשלחים לניתוח AI, והחיבור והפעילויות נשמרים. הביטול חל מאותו רגע ואילך, ואינו חל על עיבוד שכבר בוצע.
• ניתוק: ניתן לנתק את Garmin בכל עת מתוך הפרופיל. הניתוק מפסיק את הסנכרון, מוחק מיד את הקבצים המקוריים ששמורים אצלנו, ואנו מבקשים מ-Stridee לנתק את החיבור. סיכומי אימון שכבר הופקו נשמרים עד למחיקת החשבון או עד לבקשת מחיקה נפרדת.
• מחיקת חשבון: מחיקת החשבון מוחקת גם את נתוני Garmin ששמורים אצלנו, ואנו מבקשים מ-Stridee למחוק את החשבון שלך אצלה (ראו סעיף 7).
• ללא מכירה וללא פרסום: איננו מוכרים את נתוני Garmin ואיננו משתמשים בהם לפרסום.
• מי עוד מעבד את הנתונים: Stridee (החיבור ל-Garmin Connect), Render (השרתים ומסד הנתונים של המערכת), OpenAI: OpenAI API לניתוח AI, רק בהסכמתך הנפרדת, ו-Google: Gemini API לניתוח AI, רק בהסכמתך הנפרדת, ו-Firebase Cloud Messaging, שמעביר התראות הכוללות את המרחק, משך הזמן והקצב של אימון שהסתיים. ספקים אלה מעבדים את נתוני Garmin כדי לספק לנו את השירותים שלהם, בהתאם להוראותינו, ואינם משתמשים בהם למטרותיהם, למעט שימושים מוגבלים שתנאיהם מתירים: לפי תנאי ה-API בתשלום של OpenAI ושל Gemini, OpenAI ו-Google אינן משתמשות בנתונים לאימון המודלים שלהן או לשיפור מוצריהן, אך שומרות אותם לזמן מוגבל אך ורק לצורך איתור ומניעה של שימוש לרעה.
• נתונים ב-Garmin Connect: איננו משנים או מוחקים נתונים בחשבון ה-Garmin Connect שלך. שליחת אימונים ל-Garmin אינה חלק מהפיילוט.

## 10. עוגיות (Cookies)

האפליקציה משתמשת בעוגיות חיוניות בלבד לצורך שמירת מצב התחברות ופעולתיות בסיסית. איננו משתמשים בעוגיות למעקב או פרסום.

## 11. שינויים במדיניות הפרטיות

אנו עשויים לעדכן מדיניות זו מעת לעת. שינויים משמעותיים יפורסמו באפליקציה, ונעדכן את תאריך העדכון האחרון בראש המסמך.

## 12. יצירת קשר

לשאלות בנוגע למדיניות הפרטיות:
RitmoIQ — פרויקט המופעל באופן פרטי על ידי ליאור פרובקביץ
אימייל: support@ritmoiq.com
    `
  },
  terms: {
    title: "תנאי שימוש",
    content: `
עודכן לאחרונה: ${LEGAL_LAST_UPDATED.he}

## 1. מבוא

ברוכים הבאים ל-RitmoIQ ("השירות"), אפליקציית מאמן אימונים מותאם אישית. RitmoIQ הוא פרויקט המופעל באופן פרטי על ידי ליאור פרובקביץ, ואינו חברה מאוגדת ("אנחנו", "שלנו"). בגישה לשירות או בשימוש בו, את/ה מסכים/ה להיות מחויב/ת לתנאי שימוש אלה ("התנאים"). אם אינך מסכים/ה לתנאים, אנא אל תשתמש/י בשירות.

## 2. תיאור השירות

RitmoIQ מספקת שירותי אימון ריצה מבוססי בינה מלאכותית, כולל:
• תכניות אימון שבועיות מותאמות אישית
• ניתוח פעילויות ומשוב
• הכוונה לקראת מרוצים
• חיבור ל-Garmin Connect באמצעות Stridee (פיילוט סגור, למשתמשים שאושרו)
• כלים לניהול מאמן-מתאמן

## 3. שימוש בטכנולוגיית בינה מלאכותית

RitmoIQ משתמשת בשירותי בינה מלאכותית של OpenAI (OpenAI API) ושל Google (Gemini API) כדי לספק אימון, ניתוח אימונים ומשוב מותאמים אישית שנוצרים באמצעות בינה מלאכותית. הבהרות חשובות לגבי השימוש שלנו ב-AI:

### ✅ כיצד אנו משתמשים ב-AI:
• כדי לנתח את נתוני הפעילות שלך עצמך ולתת לך המלצות מותאמות אישית
• כדי ליצור תכניות אימון מותאמות לפי הפרופיל והיעדים שלך
• כדי לענות על שאלותיך בנושאי ריצה, אימון וכושר
• שיחות אימון בזמן אמת הגלויות רק לך (ולמאמן שלך, אם יש)

### ❌ מה אנחנו לא עושים:
• איננו משתמשים במידע שלך כדי לאמן מודלי AI משלנו
• איננו מוכרים את המידע שלך, ואיננו משתפים אותו עם צדדים שלישיים למטרות שיווק או פרסום
• איננו משתמשים במידע שלך למטרות אחרות מלבד מתן השירות ושיפורו, כמתואר במדיניות הפרטיות

לפרטים על המידע המעובד, כולל המידע הנשלח לספקי ה-AI, ראו את מדיניות הפרטיות.

## 4. שירותי צד שלישי

החיבור ל-Garmin Connect באמצעות Stridee תלוי בשירותי צד שלישי וכפוף לתנאים, לזמינות ולגישת ה-API של אותם ספקים. החיבור הוא לבחירתך.

### 4.1 Strava (החיבור הופסק)
החיבור ל-Strava הופסק ב-21 באוקטובר 2026, ולא ניתן עוד לחבר את Strava לשירות. איננו מקבלים מידע חדש מ-Strava. RitmoIQ אינה משתמשת במידע שהתקבל מ-Strava כדי לאמן מודלי AI משלה, ובהתאם למימוש הנוכחי אינה שולחת אותו לספקי ה-AI. לפרטים על מידע שהתקבל בעבר ועל הסרת ההרשאה של RitmoIQ בעמוד My Apps של Strava, ראו מדיניות הפרטיות, סעיף 8.

### 4.2 חיבור Garmin Connect (פיילוט סגור)
החיבור ל-Garmin Connect זמין באמצעות Stridee למשתמשים שאושרו לפיילוט הסגור, והוא לבחירתך. כשתחבר/י את Garmin:
• פעילויות הריצה שהושלמו יתקבלו ויוצגו באפליקציה
• אם אישרת גם את סעיף ה-AI, מאמן ה-AI ינתח אותן באמצעות ספקי ה-AI (OpenAI ו-Google Gemini) וישתמש בהן להמלצות ולהתאמת תוכנית האימונים שלך
• ניתן לבטל את ההסכמה לניתוח AI בכל עת מתוך הפרופיל
• ניתן לנתק את Garmin בכל עת מתוך הפרופיל
פרטים על המידע שמתקבל, נשמר ונשלח, ראו במדיניות הפרטיות, סעיף 9. שליחת אימונים ל-Garmin אינה חלק מהפיילוט.

### 4.3 ספקי שירותי AI (OpenAI API ו-Google Gemini API)
אנו משתמשים בשירותי ה-API בתשלום של OpenAI ושל Google כדי להפיק תשובות אימון, ניתוח אימונים ומשוב באמצעות AI. עיבוד המידע הנשלח ל-OpenAI API על ידי OpenAI כפוף לתנאי השימוש של OpenAI API (https://openai.com/policies/services-agreement/), ועיבוד המידע הנשלח ל-Gemini API על ידי Google כפוף לתנאי השימוש של Gemini API (https://ai.google.dev/gemini-api/terms). RitmoIQ אינה משתמשת במידע שלך כדי לאמן מודלי AI משלה. לפרטים על עיבוד המידע ראו את מדיניות הפרטיות.

### 4.4 זמינות שירותי צד שלישי
RitmoIQ אינה יכולה להבטיח את המשך הזמינות של חיבור כלשהו לצד שלישי. אם ספק משנה, מגביל או מפסיק את הגישה לשירותיו או ל-API שלו, הפונקציונליות הקשורה ב-RitmoIQ עשויה להשתנות, להיות מוגבלת או להיפסק.

## 5. אחסון ואבטחת מידע

המידע שלך מאוחסן באופן מאובטח בשרתים שלנו, עם אמצעי ההגנה הבאים:
• הצפנת תעבורת נתונים (HTTPS/TLS)
• אחסון מאובטח בבסיס נתונים עם בקרות גישה
• בדיקות אבטחה ועדכונים שוטפים
• ללא שיתוף עם צדדים שלישיים לא מורשים

## 6. אחריות המשתמש

בשימוש בשירות, את/ה מסכים/ה:
• למסור מידע מדויק על רמת הכושר והבריאות שלך
• לפנות לייעוץ רפואי לפני תחילת תכנית אימונים כאשר הדבר מתאים, ובמיוחד אם יש לך מצב רפואי, פציעה, כאב מתמשך, תסמינים מדאיגים או ספק לגבי יכולתך להתאמן
• להשתמש בהמלצות האימון של ה-AI כהכוונה, ולא כייעוץ רפואי
• לא לנסות לתמרן את מערכת ה-AI או לעשות בה שימוש לרעה
• לכבד את זכויות הקניין הרוחני של השירות
• להפסיק את הפעילות ולפנות לייעוץ רפואי מתאים אם את/ה חש/ה כאב, אי-נוחות חריגה, סחרחורת, קוצר נשימה או כל תסמין מדאיג אחר
• להשתמש בשירות למטרות חוקיות בלבד

## 7. הסתייגות והגבלת אחריות

### 7.1 אין מדובר בייעוץ רפואי
השירות מספק המלצות אימון שנוצרו באמצעות AI למטרות מידע בלבד. הוא אינו תחליף לייעוץ, אבחון או טיפול רפואי מקצועי, ואינו מתאים למצבי חירום רפואיים. במצב חירום רפואי יש לפנות מיד לשירותי החירום. כאשר הדבר מתאים, השירות אינו מחליף הכוונה של מאמן מקצועי או מוסמך. מומלץ להתייעץ עם גורם רפואי מוסמך לפני תחילת תכנית אימונים או שינוי באימונים. הדבר חשוב במיוחד אם יש לך מצב רפואי, פציעה, כאב מתמשך, תסמינים מדאיגים או ספק לגבי יכולתך להתאמן.

### 7.2 השירות מסופק "כמות שהוא"
השירות מסופק "כמות שהוא" (AS IS) ו"כפי שהוא זמין" (AS AVAILABLE). ככל שהדין מאפשר זאת, איננו מתחייבים ש:
• השירות יהיה זמין ברציפות וללא תקלות
• ההמלצות, התכניות והניתוחים שנוצרים באמצעות AI יהיו מדויקים, שלמים או נטולי שגיאות
• השימוש בשירות יוביל לתוצאה מסוימת באימונים, במרוצים או בבריאות
• השירות יתאים לכל משתמש

### 7.3 הגבלת אחריות
ככל שהדין מאפשר זאת, איננו אחראים לפציעות או לבעיות בריאות הנובעות ממילוי ההמלצות שמספק השירות. ככל שהדין מאפשר זאת, RitmoIQ לא תהיה אחראית לנזקים עקיפים, נלווים, מיוחדים או תוצאתיים, או לאובדן נתונים, הנובעים מהשימוש בשירות. אין בתנאים אלה כדי לשלול או להגביל אחריות שלא ניתן לשלול או להגביל על פי הדין, או זכויות המוקנות לך על פי דיני הגנת הצרכן הקוגנטיים.

## 8. גישה בתקופת הפיילוט ושירותים בתשלום בעתיד

RitmoIQ פועלת כיום כפיילוט סגור וחינמי. הגישה לשירות עשויה לדרוש אישור של RitmoIQ, ועשויה להיות מוגבלת או להישלל במהלך הפיילוט.
כרגע לא מוצע מנוי בתשלום דרך השירות, והמשתמשים אינם מחויבים כיום בדמי מנוי.
RitmoIQ עשויה להציע בעתיד תכניות בתשלום או שירותי מנוי. אם יוצעו שירותים בתשלום, תנאי המחיר, החיוב, החידוש, הביטול וההחזרים הרלוונטיים יוצגו למשתמשים לפני רכישה או הרשמה לשירותים אלה, ותנאים אלה עשויים להתעדכן בהתאם.

## 9. סיום השימוש ומחיקת חשבון

תוכל/י למחוק את החשבון שלך בכל עת מתוך האפליקציה (פרופיל ← מחיקת חשבון) או בפנייה אלינו בכתובת support@ritmoiq.com. לאחר המחיקה לא ניתן לשחזר את החשבון. המחיקה כוללת את נתוני החשבון שלך ב-RitmoIQ ואת חשבון ההתחברות שלך, כמתואר במדיניות הפרטיות, לרבות לגבי גיבויים. הסרת ההרשאה של RitmoIQ ב-Strava או ניתוק Garmin אינם מוחקים בעצמם מידע שכבר נשמר אצלנו. אנו שומרים לעצמנו את הזכות להשעות או לסגור חשבונות המפרים תנאים אלה.

## 10. שינויים בתנאים

אנו עשויים לעדכן תנאים אלה מעת לעת. על שינויים משמעותיים נודיע למשתמשים במייל או בהודעה באפליקציה. המשך השימוש בשירות לאחר השינויים מהווה הסכמה לתנאים המעודכנים.

## 11. יצירת קשר

לשאלות על תנאים אלה או על השירות, פנו אלינו בכתובת: support@ritmoiq.com

## 12. דין חל וסמכות שיפוט

תנאים אלה כפופים לחוקי מדינת ישראל. כל סכסוך הנוגע לתנאים אלה או לשירות יידון בבתי המשפט המוסמכים בתל אביב-יפו.

## סיכום הנקודות העיקריות

✅ המידע שלך משמש למתן ולשיפור שירות האימון שלך, כמתואר במדיניות הפרטיות
• 🤖 האימון מבוסס ה-AI נוצר באמצעות OpenAI API ו-Google Gemini API
• 🔗 מקור האימונים הוא Garmin Connect באמצעות Stridee (פיילוט סגור, לבחירתך); החיבור ל-Strava הופסק. החיבורים תלויים בזמינות של צדדים שלישיים
❌ איננו מאמנים מודלי AI משלנו באמצעות המידע שלך
❌ איננו מוכרים את המידע שלך ואיננו משתפים אותו למטרות שיווק או פרסום
• 🔒 ניתן למחוק את החשבון בכל עת דרך פרופיל ← מחיקת חשבון
    `
  }
};

// ==================== LEGAL MODAL COMPONENT ====================

// E-mail addresses and URLs in the (RTL) legal text render as isolated LTR runs, so they never reorder or merge
// with neighbouring Hebrew or punctuation (2026-10-21).
const LTR_RUN = /(https?:\/\/[^\s)]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/;
export function withLtrRuns(text) {
  return text.split(LTR_RUN).map((part, i) => (i % 2 === 1 ? <bdi key={i} dir="ltr">{part}</bdi> : part));
}

const LegalModal = ({ isOpen, onClose, docType }) => {
  if (!isOpen || !docType) return null;
  
  const doc = LEGAL_DOCS[docType];
  
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{doc.title}</h2>
          <button style={styles.closeButton} onClick={onClose}>✕</button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.documentText}>
            {doc.content.split('\n').map((line, i) => {
              if (line.startsWith('## ')) {
                return <h2 key={i} style={styles.docH2}>{line.replace('## ', '')}</h2>;
              } else if (line.startsWith('### ')) {
                return <h3 key={i} style={styles.docH3}>{line.replace('### ', '')}</h3>;
              } else if (line.startsWith('• ')) {
                return <p key={i} style={styles.docBullet}>{withLtrRuns(line)}</p>;
              } else if (line.startsWith('❌ ') || line.startsWith('✅ ')) {
                return <p key={i} style={styles.docBullet}>{withLtrRuns(line)}</p>;
              } else if (line.trim()) {
                return <p key={i} style={styles.docParagraph}>{withLtrRuns(line)}</p>;
              }
              return null;
            })}
          </div>
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.understandButton} onClick={onClose}>
            הבנתי ✓
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN CONSENT SCREEN ====================

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

const ConsentScreen = ({ onConsent, userId = null, userEmail = null }) => {
  const [checks, setChecks] = useState({
    health: false,
    privacy: false,
    terms: false,
    responsibility: false
  });
  const [modalDoc, setModalDoc] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submitError, setSubmitError] = useState(null);

  const allChecked = Object.values(checks).every(v => v);

  const handleCheckChange = (key) => {
    setChecks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Consent is granted ONLY when the backend records it (authenticated POST, current version).
  // Every failure keeps the user on this screen; nothing here can unlock the app on its own.
  const handleSubmit = async () => {
    if (!allChecked || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    let response;
    try {
      response = await authFetch(`${API_BASE_URL}/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          consent_version: CONSENT_VERSION,
          health_accepted: checks.health,
          privacy_accepted: checks.privacy,
          terms_accepted: checks.terms,
          responsibility_accepted: checks.responsibility
        })
      });
    } catch (err) {
      console.warn('Could not reach server to save consent', err);
      setSubmitError('לא הצלחנו להתחבר לשרת כדי לשמור את ההסכמה. בדוק/י את החיבור ונסה/י שוב.');
      setIsSubmitting(false);
      return;
    }

    if (response.ok) {
      let data = {};
      try { data = await response.json(); } catch { data = {}; }
      if (data.success) {
        setIsSubmitting(false);
        onConsent();
        return;
      }
      setSubmitError('שמירת ההסכמה לא הושלמה. נסה/י שוב.');
    } else if (response.status === 401 || response.status === 403) {
      setSubmitError('לא הצלחנו לאמת את זהותך. התחבר/י מחדש ונסה/י שוב.');
    } else if (response.status === 409) {
      setSubmitError('קיימת גרסה חדשה של האפליקציה ושל מסמכי ההסכמה. רענן/י את הדף כדי להמשיך.');
    } else if (response.status === 422) {
      setSubmitError('יש לאשר את כל הסעיפים כדי להמשיך.');
    } else {
      setSubmitError('אירעה שגיאה בשמירת ההסכמה. נסה/י שוב.');
    }
    setIsSubmitting(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <img src="/icons/ritmoiq-r.png" alt="" aria-hidden="true" style={styles.logo} />
          <h1 style={styles.title}>ברוך הבא ל-RitmoIQ!</h1>
          <p style={styles.subtitle}>המאמן האישי שלך מבוסס בינה מלאכותית</p>
        </div>

        {/* Warning Box */}
        <div style={styles.warningBox}>
          <div>
            <strong>חשוב לדעת:</strong>
            <p style={styles.warningText}>
              האפליקציה מספקת המלצות אימון כלליות בלבד ואינה תחליף 
              לייעוץ רפואי מקצועי או מאמן ריצה מוסמך.
            </p>
          </div>
        </div>

        {/* Checkboxes */}
        <div style={styles.checkboxSection}>
          
          {/* Health Disclaimer */}
          <div style={styles.checkboxRow}>
            <input
              type="checkbox"
              id="health"
              checked={checks.health}
              onChange={() => handleCheckChange('health')}
              style={styles.checkbox}
            />
            <label htmlFor="health" style={styles.checkboxLabel}>
              קראתי ואני מסכים/ה ל
              <button 
                style={styles.linkButton}
                onClick={() => setModalDoc('health')}
              >
                הצהרת הבריאות
              </button>
            </label>
          </div>

          {/* Privacy Policy */}
          <div style={styles.checkboxRow}>
            <input
              type="checkbox"
              id="privacy"
              checked={checks.privacy}
              onChange={() => handleCheckChange('privacy')}
              style={styles.checkbox}
            />
            <label htmlFor="privacy" style={styles.checkboxLabel}>
              קראתי ואני מסכים/ה ל
              <button 
                style={styles.linkButton}
                onClick={() => setModalDoc('privacy')}
              >
                מדיניות הפרטיות
              </button>
            </label>
          </div>

          {/* Terms of Service */}
          <div style={styles.checkboxRow}>
            <input
              type="checkbox"
              id="terms"
              checked={checks.terms}
              onChange={() => handleCheckChange('terms')}
              style={styles.checkbox}
            />
            <label htmlFor="terms" style={styles.checkboxLabel}>
              קראתי ואני מסכים/ה ל
              <button 
                style={styles.linkButton}
                onClick={() => setModalDoc('terms')}
              >
                תנאי השימוש
              </button>
            </label>
          </div>

          {/* Personal Responsibility */}
          <div style={styles.checkboxRow}>
            <input
              type="checkbox"
              id="responsibility"
              checked={checks.responsibility}
              onChange={() => handleCheckChange('responsibility')}
              style={styles.checkbox}
            />
            <label htmlFor="responsibility" style={styles.checkboxLabel}>
              אני מבין/ה שההחלטה אם להתאמן היא באחריותי, ואתייעץ עם רופא כשיש לי מצב רפואי, פציעה או ספק לגבי יכולתי להתאמן
            </label>
          </div>

        </div>

        {submitError && (
          <p role="alert" style={styles.errorText}>{submitError}</p>
        )}

        {/* Submit Button */}
        <button
          style={{
            ...styles.submitButton,
            ...(allChecked ? styles.submitButtonEnabled : styles.submitButtonDisabled)
          }}
          onClick={handleSubmit}
          disabled={!allChecked || isSubmitting}
        >
          {isSubmitting ? 'רק רגע...' : 'בואו נתחיל!'}
        </button>

        {/* Footer */}
        <p style={styles.footer}>
          גרסה {CONSENT_VERSION} | RitmoIQ
        </p>
      </div>

      {/* Legal Document Modal */}
      <LegalModal
        isOpen={modalDoc !== null}
        onClose={() => setModalDoc(null)}
        docType={modalDoc}
      />
    </div>
  );
};

// ==================== STYLES ====================

const styles = {
  errorText: {
    color: 'var(--g-sub)',
    background: 'var(--g-pressed)',
    border: '1px solid var(--g-line)',
    borderRadius: '10px',
    padding: '10px 12px',
    fontSize: '14px',
    margin: '0 0 12px',
    textAlign: 'center'
  },
  container: {
    minHeight: '100vh',
    background: 'var(--g-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Heebo', system-ui, sans-serif",
    direction: 'rtl'
  },
  card: {
    background: 'var(--g-card)',
    border: '1px solid var(--g-line)',
    borderRadius: '16px',
    padding: '28px 20px',
    maxWidth: '500px',
    width: '100%'
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px'
  },
  logo: {
    width: '56px',
    height: '56px',
    borderRadius: '14px',
    objectFit: 'cover',
    display: 'block',
    margin: '0 auto 14px'
  },
  title: {
    fontSize: '28px',
    fontWeight: 800,
    color: 'var(--g-text)',
    margin: '0 0 8px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--g-muted)',
    margin: 0
  },
  warningBox: {
    background: 'var(--g-bg)',
    border: '1px solid var(--g-line)',
    color: 'var(--g-text)',
    borderRadius: '12px',
    padding: '15px',
    display: 'flex',
    gap: '12px',
    marginBottom: '25px',
    alignItems: 'flex-start'
  },
  warningText: {
    margin: '5px 0 0 0',
    fontSize: '14px',
    color: 'var(--g-sub)',
    lineHeight: '1.5'
  },
  checkboxSection: {
    marginBottom: '25px'
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid var(--g-line)'
  },
  checkbox: {
    width: '20px',
    height: '20px',
    marginTop: '2px',
    cursor: 'pointer',
    accentColor: 'var(--g-accent)'
  },
  checkboxLabel: {
    fontSize: '15px',
    color: 'var(--g-text)',
    lineHeight: '1.5',
    cursor: 'pointer'
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: 'var(--g-sub)',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: '15px',
    padding: '0 4px'
  },
  submitButton: {
    width: '100%',
    padding: '16px',
    fontSize: '18px',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '999px',
    minHeight: '52px',
    cursor: 'pointer',
    fontFamily: "'Heebo', system-ui, sans-serif"
  },
  submitButtonEnabled: {
    background: 'var(--g-accent)',
    color: 'var(--g-on-accent)'
  },
  submitButtonDisabled: {
    background: 'var(--g-bar)',
    color: 'var(--g-muted)',
    cursor: 'not-allowed'
  },
  footer: {
    textAlign: 'center',
    color: 'var(--g-muted)',
    fontSize: '12px',
    marginTop: '20px'
  },
  
  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modalContent: {
    background: 'var(--g-card)',
    border: '1px solid var(--g-line)',
    borderRadius: '16px',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    direction: 'rtl'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid var(--g-line)'
  },
  modalTitle: {
    margin: 0,
    fontSize: '22px',
    color: 'var(--g-text)'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: 'var(--g-sub)',
    padding: '5px 10px',
    minWidth: '44px',
    minHeight: '44px'
  },
  modalBody: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1
  },
  documentText: {
    fontSize: '14px',
    lineHeight: '1.7',
    color: 'var(--g-sub)'
  },
  docH2: {
    fontSize: '18px',
    color: 'var(--g-text)',
    marginTop: '25px',
    marginBottom: '15px',
    paddingBottom: '8px',
    borderBottom: '1px solid var(--g-line)'
  },
  docH3: {
    fontSize: '16px',
    color: 'var(--g-text)',
    marginTop: '20px',
    marginBottom: '10px'
  },
  docParagraph: {
    marginBottom: '12px'
  },
  docBullet: {
    marginBottom: '8px',
    paddingRight: '10px'
  },
  modalFooter: {
    padding: '15px 20px',
    borderTop: '1px solid var(--g-line)',
    textAlign: 'center'
  },
  understandButton: {
    background: 'var(--g-accent)',
    color: 'var(--g-on-accent)',
    border: 'none',
    padding: '12px 40px',
    minHeight: '48px',
    fontSize: '16px',
    borderRadius: '999px',
    cursor: 'pointer',
    fontWeight: 'bold'
  }
};

export default ConsentScreen;
